import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { awsIntegratedKeys, integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import aws_member_1 from "./images/aws_member_1.png";
import aws_member_2 from "./images/aws_member_2.png";
import aws_member_3 from "./images/aws_member_3.png";
import aws_member_4 from "./images/aws_member_4.png";
import aws_member_5 from "./images/aws_member_5.png";
import PermissionsSummary from "./PermissionSummary";

const AWSMemberOrganization = ({
  awsRegionOptions,
  userPermissions,
  guideMode,
}) => {
  const { isIntegratedLoading } = useIsIntegrated("aws");
  const [doneCheckingIntegrationDetails, setDoneCheckingIntegrationDetails] =
    useState(false);
  const [breezeAccountID, setBreezeAccountID] = useState("");
  const [roleName, setRoleName] = useState("BreezeSecurity-Role");
  const queryClient = useQueryClient();
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [externalId, setExternalId] = useState("");

  const [checkConnectionButtonLoading, setCheckConnectionButtonLoading] =
    useState(false);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const colors = useColors();
  const {
    registerAWSMembersAccounts,
    checkAWSRootAccountIsIntegrated,
    checkAWSMemberAccountAreIntegrated,
    fetchAWSAccountID,
    getCloudFormationURL,
    getRootExternalId,
  } = useAPI();

  const { data: CloudFormationPublicURL } = useQuery({
    queryKey: awsIntegratedKeys.cloudFormation(),
    queryFn: getCloudFormationURL,
    select: (res) => res.data,
  });

  const breezeDefaultStackName = "BreezePlatform";
  const stackSetRouteURL =
    "https://console.aws.amazon.com/cloudformation/home#/stacksets/create?stackSetName=" +
    encodeURIComponent(breezeDefaultStackName);

  const handleLinkClick = () => {
    // Open the IAM console in a new tab with the specified role ARN
    window.open(stackSetRouteURL, "_blank");
  };

  const handleCheckConnection = () => {
    let roleNameTrimmed = roleName.trim();
    setCheckConnectionButtonLoading(true);
    registerAWSMembersAccounts(roleNameTrimmed).then((response) => {
      if (response.data === 200) {
        setCheckConnectionButtonLoading(false);
        Toast.success("AWS member accounts are integrated");
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        queryClient.invalidateQueries({
          queryKey: awsIntegratedKeys.memberAccounts(),
        });
        queryClient.invalidateQueries({
          queryKey: awsIntegratedKeys.rootAccount(),
        });
      } else {
        Toast.error(
          "An error occurred. Please verify that the role has been created in the master accounts."
        );
      }
    });
  };

  const { data: AWSRootAccountIsIntegrated, isLoading: rootAccountLoading } =
    useQuery({
      queryKey: awsIntegratedKeys.rootAccount(),
      queryFn: checkAWSRootAccountIsIntegrated,
      select: (res) => res.data,
    });

  const { isLoading: memberAccountLoading } = useQuery({
    queryKey: awsIntegratedKeys.memberAccounts(),
    queryFn: checkAWSMemberAccountAreIntegrated,
    select: (res) => res.data,
  });

  const { data: rootAccount } = useQuery({
    queryKey: awsIntegratedKeys.rootAccount(),
    queryFn: checkAWSRootAccountIsIntegrated,
    select: (res) => res.data,
  });

  const { data: rootExternalId } = useQuery({
    queryKey: awsIntegratedKeys.rootExternalId(),
    queryFn: getRootExternalId,
    enabled: !!AWSRootAccountIsIntegrated,
    select: (res) => res.data.external_id,
  });

  useEffect(() => {
    // Only fetch AWS account ID if root account is integrated
    if (AWSRootAccountIsIntegrated) {
      fetchAWSAccountID().then((response) => {
        setBreezeAccountID(response.data.account_id);
        // Use the external ID from the root account
        setExternalId(rootExternalId);
      });
    }
  }, [AWSRootAccountIsIntegrated, rootExternalId]);

  useEffect(() => {
    if (!memberAccountLoading && !rootAccountLoading && !isIntegratedLoading) {
      setDoneCheckingIntegrationDetails(true);
    }
  }, [memberAccountLoading, rootAccountLoading, isIntegratedLoading]);

  const handleRegionChange = (newValue) => {
    if (!newValue) {
      setSelectedRegion(null);
      return;
    }
    setSelectedRegion(newValue);
  };

  return (
    <Box mt={2}>
      {doneCheckingIntegrationDetails ? (
        <>
          {!AWSRootAccountIsIntegrated && (
            <>
              <Typography variant="h6">
                Integrate the Root AWS account first
              </Typography>
            </>
          )}
          {AWSRootAccountIsIntegrated && (
            <>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mt="20px" mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Sign in to the Root AWS account"></GuidelineStep>
                <GuidelineStep title="Ensure you have the necessary AWS permissions">
                  <Accordion
                    sx={{ width: "700px", backgroundColor: "#f5f5f5" }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      {" "}
                      <Typography
                        variant="subtitle2"
                        color={colors.primary_darker}
                      >
                        AWS required permissions
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {userPermissions.map((permission, index) => (
                        <Typography key={index}>{permission}</Typography>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                </GuidelineStep>

                <GuidelineStep title="Run the following AWS CloudFormation Stack Set to create an IAM Role for Breeze Security">
                  <>
                    <Box>
                      <Typography variant={"body2"} mb={2}>
                        Select the AWS region where you want to create the
                        stack.
                      </Typography>
                      <Autocomplete
                        sx={{
                          color: colors.text_primary,
                          borderRadius: "10px",
                          "& .MuiAutocomplete-clearIndicator": {
                            display: "none",
                          },
                        }}
                        id="aws-region-autocomplete"
                        options={awsRegionOptions}
                        getOptionLabel={(option) =>
                          `${option.name} (${option.code})`
                        }
                        value={selectedRegion}
                        onChange={(event, newValue) =>
                          handleRegionChange(newValue)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="AWS Region"
                            variant="outlined"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                height: "50px",
                                width: "300px",
                                "& fieldset": {
                                  borderColor: colors.divider2,
                                },
                                "&:hover fieldset": {
                                  borderColor: colors.divider2,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.divider2,
                                },
                              },
                              "& .MuiInputBase-input": {
                                fontSize: "14px",
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: "14px",
                              },
                            }}
                            disabled={guideMode}
                          />
                        )}
                        ListboxProps={{
                          sx: {
                            "& .MuiAutocomplete-option": {
                              fontSize: "14px",
                            },
                          },
                        }}
                        componentsProps={{
                          popupIndicator: {
                            disableRipple: true,
                          },
                        }}
                      />
                    </Box>
                    <PrimaryButton
                      sx={{
                        height: "36px",
                        borderRadius: "10px",
                        width: "260px",
                        textTransform: "none",
                      }}
                      onClick={handleLinkClick}
                      disabled={!selectedRegion || guideMode}
                    >
                      Deploy CloudFormation Stack
                    </PrimaryButton>
                    <PermissionsSummary />
                  </>
                </GuidelineStep>
                <GuidelineStep title="Fill the template url and click Next">
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant={"body2"}>
                      Template URL: <strong>{CloudFormationPublicURL}</strong>
                    </Typography>
                    <CustomCopyButton
                      copyTitle={"URL"}
                      copyText={CloudFormationPublicURL}
                    />
                  </Box>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: aws_member_1,
                        altext: "AWS CloudFormation Filling",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Specify StackSet details">
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant={"body2"}>
                      StackSet name, f.e{" "}
                      <strong>{breezeDefaultStackName}</strong>{" "}
                    </Typography>{" "}
                    <CustomCopyButton
                      copyTitle={"URL"}
                      copyText={breezeDefaultStackName}
                    />
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant={"body2"}>
                      BreezeAWS: <strong>{breezeAccountID}</strong>
                    </Typography>{" "}
                    <CustomCopyButton
                      copyTitle={"AWS Account ID"}
                      copyText={breezeAccountID}
                    />
                  </Box>
                  <Typography variant={"body2"}>
                    BreezeRoleName: Name of the role that will be created for
                    Breeze to assume (default:{" "}
                    <strong>BreezeSecurity-Role</strong>)
                  </Typography>

                  {/* Conditionally render externalId */}
                  {externalId && (
                    <>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant={"body2"}>
                          ExternalId: <strong>{externalId}</strong>
                        </Typography>{" "}
                        <CustomCopyButton
                          copyTitle={"External ID"}
                          copyText={externalId}
                        />
                      </Box>

                      <Typography
                        variant={"body2"}
                        color={colors.warning_darker}
                      >
                        Important: Do not modify the External ID. All member
                        accounts must use the same External ID as the root
                        account.
                      </Typography>
                    </>
                  )}
                  <ScreenshotAccordion
                    images={[
                      {
                        file: aws_member_2,
                        altext: "AWS CloudFormation Filling",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Configure StackSet options">
                  <Typography variant={"body2"}>
                    a. Add Tags if needed
                  </Typography>
                  <Typography variant={"body2"}>
                    b. Execution configuration: <strong>Active</strong>
                  </Typography>
                  <Typography variant={"body2"}>
                    c. Down below on Capabilities check the box and click Next
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: aws_member_3,
                        altext: "AWS CloudFormation Filling",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Set deployment options">
                  <Typography variant={"body2"}>
                    Deployment targets: <strong>Deploy to organization</strong>
                  </Typography>
                  <Typography variant={"body2"}>
                    Specify regions: Choose the region where the stack will be
                    created, f.e Europe (Ireland): eu-west-1
                  </Typography>
                  <ScreenshotAccordion
                    itemId={1}
                    images={[
                      {
                        file: aws_member_4,
                        altext: "Deployment options",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Maximum concurrent accounts:{" "}
                    <strong>Percentage - 100%</strong>
                  </Typography>
                  <Typography variant={"body2"}>
                    Failure tolerance: <strong>Percentage - 100%</strong>
                  </Typography>
                  <ScreenshotAccordion
                    itemId={2}
                    images={[
                      {
                        file: aws_member_5,
                        altext: "AWS CloudFormation Creation",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Click submit to create the stack"></GuidelineStep>
                <GuidelineStep title="If you changed the Role name, update it in the text field below">
                  <TextField
                    label="Role Name"
                    value={roleName}
                    onChange={(event) => setRoleName(event.target.value)}
                    disabled={guideMode}
                  />{" "}
                </GuidelineStep>

                <GuidelineStep title="Wait for the stack to be created, then click to verify the connection">
                  <Typography variant={"body2"}>
                    The connection verification can take several minutes.
                  </Typography>
                  <Typography variant={"body2"}>
                    After successful verification, the AWS member accounts will
                    be shown in the <strong>Tab 4. Manage Accounts</strong> tab.
                  </Typography>
                  <PrimaryButton
                    onClick={handleCheckConnection}
                    disabled={guideMode}
                  >
                    {checkConnectionButtonLoading ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <span>Checking Connection...</span>
                        <LoadingIcon sx={{ color: "white" }} size={20} />
                      </Box>
                    ) : (
                      "Check Connection"
                    )}
                  </PrimaryButton>
                </GuidelineStep>
              </GuidelineStepByStep>
            </>
          )}
          {/* This is currently not used and will be refactored in the future */}
          {/*
           {AWSRootAccountIsIntegrated && AWSMemberAccountIsIntegrated && (
            <>
              <Typography variant="h6">
                AWS Member Accounts are integrated
              </Typography>
              <br></br>
              <Typography variant="subtitle2">
                Use: <br />
                <strong>Tab 4. Manage Accounts</strong> to manage the accounts
                (Regions, Nickname, etc.)
              </Typography>
            </>
          )} */}
        </>
      ) : (
        <LoadingIcon />
      )}
    </Box>
  );
};

export default AWSMemberOrganization;
